import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
    FraudReportApiService, FraudReportFilterRequest, FraudReportsResponse, FraudReportStatus, FraudReportType,
    ReferenceBookResponse, ReferenceBookType,
} from '@npck/af-frontend-api';
import {
    DATE_SERVER_FORMAT,
    DATE_TIME_SERVER_FORMAT,
    DATE_UI_FORMAT,
    RouterService,
    rxSpinner,
    TAB,
} from '@npck/libs/commons';
import dayjs from 'dayjs';
import {combineLatest} from 'rxjs';
import {
    fieldExtractor,
    FilterDictionaryField,
    FilterField, getFormattedDateOrNull,
} from '../../../components/full-table/filter/filter.component';
import {mergeTableEvent, TableEvent} from '../../../components/full-table/full-table.component';
import {
    Column,
    ColumnType,
    Row,
    StatusColumn,
} from '../../../components/full-table/table/table.component';
import {mapFraudReportStatusToStyle} from '../../../components/status/status.component';
import {AuthHolderService} from '../../../domain/auth-holder.service';
import {DictionaryHolderService, findValueInDictionary} from '../../../domain/dictionary-holder.service';
import {FraudReportsFilterHolderService} from '../../../domain/fraud-reports-filter-holder.service';
import {FRAUD_REPORT_ID, FRAUD_REPORT_TYPE} from '../../../domain/query-params.keys';
import {Path} from '../../../path';
import {ANALYTIC_FRAUD_REPORTS_TAB} from '../analytics.component';

@Component({
    selector: 'af-analytic-fraud-reports',
    templateUrl: 'fraud-reports.component.html',
    standalone: false
})
export class FraudReportsComponent implements OnInit, OnDestroy {
    Path = Path;
    tableEvent: TableEvent;
    filterFields: FilterField[] = [];
    dataSource: Row[] = [];
    totalElements: number;
    isDestroyed = false;
    reportType: FraudReportType;

    constructor(
        private activatedRoute: ActivatedRoute,
        public filterHolderService: FraudReportsFilterHolderService,
        private fraudReportApiService: FraudReportApiService,
        private routerService: RouterService,
        private dictionaryHolderService: DictionaryHolderService,
        private authHolderService: AuthHolderService,
    ) {
        this.filterFields = [
            {
                type: 'text',
                name: 'query',
            },
            <FilterField>{
                type: 'dateRange',
                name: 'fraudReportDateRange',
            },
            <FilterDictionaryField>{
                isMultiSelect: true,
                type: 'select',
                name: 'fraudReportStatus',
                dictionaryName: 'fraud_report_status',
                values: [],
            },
            ...(this.authHolderService.hasAccess(['npck-admin']) ? [
                <FilterDictionaryField>{
                    isMultiSelect: true,
                    type: 'select',
                    name: 'organization',
                    dictionaryName: 'bin',
                    filterBy: ['BANK', 'PAYM', 'MFO'],
                    values: [],
                }
            ] : []),
            <FilterField>{
                type: 'dateRange',
                name: 'separatedDateRange',
            },
            <FilterField>{
                type: 'dateRange',
                name: 'fraudReportUpdatedDateRange',
            },
            <FilterField>{
                type: 'dateRange',
                name: 'fraudReportSentDateRange',
            }
        ];
    }

    ngOnInit(): void {
        this.reportType = this.activatedRoute.snapshot.paramMap.get(FRAUD_REPORT_TYPE) as FraudReportType;
        this.loadFraudReports(this.filterHolderService.getState().tableEvent);
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;
    }

    loadFraudReports(tableEvent: TableEvent): void {
        this.tableEvent = mergeTableEvent(this.filterHolderService.getState().tableEvent, tableEvent);
        this.filterHolderService.storeTableState(this.tableEvent);
        if (this.tableEvent.spinnerId !== 'paging') {
            this.dataSource = [];
            this.totalElements = 0;
        }

        combineLatest([
            this.dictionaryHolderService.getReference(ReferenceBookType.FRAUD_REPORT_STATUS),
            this.dictionaryHolderService.getReference(ReferenceBookType.BIN),
            this.fraudReportApiService.getFraudReports(this.buildFilterRequest())
        ])
            .pipe(rxSpinner(this.tableEvent.spinnerId))
            .subscribe(([fraudReportStatuses, bins, fraudReportsResponse]) => {
                this.filterHolderService.storeResponse(fraudReportsResponse);
                this.buildDataSource(fraudReportStatuses, bins, fraudReportsResponse);
            });
    }

    private buildFilterRequest(): FraudReportFilterRequest {
        return {
            search: this.tableEvent.filter?.query,
            reportType: this.reportType,
            periodDateFrom: getFormattedDateOrNull(
                this.tableEvent.filter?.fraudReportDateRange?.dateFrom,
                DATE_SERVER_FORMAT,
            ),
            periodDateTo: getFormattedDateOrNull(
                this.tableEvent.filter?.fraudReportDateRange?.dateTo,
                DATE_SERVER_FORMAT,
            ),
            statuses: fieldExtractor(this.tableEvent.filter?.fraudReportStatus) as FraudReportStatus[],
            organizations: this.authHolderService.hasAccess(['npck-admin']) ? fieldExtractor(this.tableEvent.filter?.organization) : [],
            createDateFrom: getFormattedDateOrNull(
                this.tableEvent.filter?.separatedDateRange?.startDate?.date,
                DATE_TIME_SERVER_FORMAT,
            ),
            createDateTo: getFormattedDateOrNull(
                this.tableEvent.filter?.separatedDateRange?.endDate?.date,
                DATE_TIME_SERVER_FORMAT,
            ),
            updatedDateFrom: getFormattedDateOrNull(
                this.tableEvent.filter?.fraudReportUpdatedDateRange?.startDate?.date,
                DATE_TIME_SERVER_FORMAT,
            ),
            updatedDateTo: getFormattedDateOrNull(
                this.tableEvent.filter?.fraudReportUpdatedDateRange?.endDate?.date,
                DATE_TIME_SERVER_FORMAT,
            ),
            sentDateFrom: getFormattedDateOrNull(
                this.tableEvent.filter?.fraudReportSentDateRange?.startDate?.date,
                DATE_TIME_SERVER_FORMAT,
            ),
            sentDateTo: getFormattedDateOrNull(
                this.tableEvent.filter?.fraudReportSentDateRange?.endDate?.date,
                DATE_TIME_SERVER_FORMAT,
            ),
            limit: this.tableEvent.page?.pageSize,
            offset: this.tableEvent.page?.pageOffset,
            sortBy: this.tableEvent.sortBy?.columnName,
            sortOrder: this.tableEvent.sortBy?.direction,
        } as FraudReportFilterRequest;
    }

    private buildDataSource(
        fraudReportStatuses: ReferenceBookResponse[],
        bins: ReferenceBookResponse[],
        response: FraudReportsResponse
    ): void {
        this.totalElements = response.total;
        this.dataSource = response.items.map(item => <Row>{
            click: () => this.openReportDetails(item.id),
            period: <Column>{
                type: ColumnType.TEXT,
                sortable: true,
                text: [item.periodFrom, item.periodTo].filter(n => !!n)
                    .map(n => dayjs(n).format(DATE_UI_FORMAT)).join(' - '),
            },
            status: <StatusColumn>{
                type: ColumnType.STATUS,
                sortable: true,
                text: findValueInDictionary(fraudReportStatuses, item.reportStatus, 'name_ru') ?? '-',
                style: mapFraudReportStatusToStyle(item.reportStatus),
            },
            organization: <Column>{
                type: ColumnType.TEXT,
                text: findValueInDictionary(bins, item.bin, 'name_ru') ?? '-',
            },
            createdDate: <Column>{
                type: ColumnType.DATE_TIME_SECONDS,
                sortable: true,
                text: item.createdDate,
            },
            updatedDate: <Column>{
                type: ColumnType.DATE_TIME_SECONDS,
                sortable: true,
                text: item.updatedDate,
            },
            signedAndSentDate: <Column>{
                type: ColumnType.DATE_TIME_SECONDS,
                sortable: true,
                text: item.signedAndSentDate,
            },
            id: <Column>{
                type: ColumnType.TEXT,
                text: item.id,
                cellStyle: {
                    ellipsisTextLarge: true,
                },
            },
        });
    }

    private openReportDetails(fraudReportId: string): void {
        this.routerService.navigate(Path.ANALYTICS_FRAUD_REPORT_DETAILS, {
            pathParams: {
                [FRAUD_REPORT_TYPE]: this.reportType,
                [FRAUD_REPORT_ID]: fraudReportId,
            },
            queryParams: {
                [TAB]: ANALYTIC_FRAUD_REPORTS_TAB,
            }
        });
    }
}
